import { docparserVlmHopIdSchema } from '@lib/docparser/types/docparser-vlm-hop-id';
import { stagedFlowUploadPathSchema } from '@lib/files/utils/validate-staged-flow-upload-path';
import { MAX_UPLOAD_SIZE_BYTES } from '@lib/shared/constants/max-upload-size';
import { z } from 'zod';

import { FLOW_UPLOAD_MIME } from '../constants/flow-upload';

/** Max files per begin/accept call (bounds concurrent downloads per request). */
export const FLOW_UPLOAD_BATCH_MAX = 10;

/** Every staged path must sit under this deal/package (blocks foreign objects). */
export function refineStagedPathsInPackage(
  value: {
    dealId: string;
    packageId: string;
    files: readonly { storagePath: string }[];
  },
  ctx: z.RefinementCtx
): void {
  const pathSchema = stagedFlowUploadPathSchema(value.dealId, value.packageId);
  for (const [index, file] of value.files.entries()) {
    if (!pathSchema.safeParse(file.storagePath).success) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid staged upload path',
        path: ['files', index, 'storagePath'],
      });
    }
  }
}

const uploadIdentifiers = z.object({
  dealId: z.uuid('Invalid deal ID'),
  packageId: z.uuid('Invalid package ID'),
  flowId: z.uuid('Invalid flow ID').optional(),
});

const beginFileSchema = z.object({
  name: z.string().min(1),
  size: z.number().int().positive().max(MAX_UPLOAD_SIZE_BYTES),
  contentType: z.literal(FLOW_UPLOAD_MIME),
});

export const beginFlowUploadSchema = uploadIdentifiers.extend({
  // Capped so the process route can't be handed an unbounded batch to
  // download concurrently (OOM / timeout guard). The client sends one file
  // per request; the cap only bounds abuse.
  files: z.array(beginFileSchema).min(1).max(FLOW_UPLOAD_BATCH_MAX),
});

const processFileSchema = z.object({
  storagePath: z.string().min(1),
  name: z.string().min(1),
});

export const processFlowUploadSchema = uploadIdentifiers
  .extend({
    uploadSource: z.string().optional(),
    skipParserCacheRead: z.boolean().optional().default(false),
    skipParserCacheWrite: z.boolean().optional().default(false),
    docparserEnabled: z.boolean().optional(),
    docparserVlmHopOverride: docparserVlmHopIdSchema.optional(),
    /** Upload the PDF ready for manual fields; do not run field detection. */
    skipParse: z.boolean().optional().default(false),
    files: z.array(processFileSchema).min(1).max(FLOW_UPLOAD_BATCH_MAX),
  })
  .superRefine(refineStagedPathsInPackage);

export type BeginFlowUploadInput = z.infer<typeof beginFlowUploadSchema>;
